import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import {
  Card,
  Stack,
  Typography,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  TextField,
  Box,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import 'moment/locale/tr';
import DynamicTable from 'src/components/shared/DynamicTable';
import Services from 'src/services/Services';
import { useLocalization } from 'src/context/LocalizationContext';

const formatWorkingHours = (hours) => {
  const days = {
    monday: 'Pazartesi',
    tuesday: 'Salı',
    wednesday: 'Çarşamba',
    thursday: 'Perşembe',
    friday: 'Cuma',
    saturday: 'Cumartesi',
    sunday: 'Pazar',
  };

  return Object.entries(hours).map(([day, schedule]) => (
    <Typography key={day} variant="body2" sx={{ mb: 0.5 }}>
      <span style={{ fontWeight: 'bold' }}>{days[day]}:</span> {schedule.open ? `${schedule.start} - ${schedule.end}` : 'Kapalı'}
    </Typography>
  ));
};

export default function ContactFormsPage() {
  const { language } = useLocalization();

  const [loading, setLoading] = useState(true);
  const [contactForms, setContactForms] = useState([]);
  const [allContactForms, setAllContactForms] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFormData, setSelectedFormData] = useState(null);
  const [cityFilter, setCityFilter] = useState('');
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();

  const handleOpenModal = (rowData) => {
    setSelectedFormData(rowData.dataJSON);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedFormData(null);
  };

  const handleFilter = () => {
    let filteredData = [...allContactForms];

    if (cityFilter) {
      filteredData = filteredData.filter(form =>
        form.dataJSON.city.toLowerCase().includes(cityFilter.toLowerCase())
      );
    }

    if (startDate && endDate) {
        const start = moment(startDate).startOf('day');
        const end = moment(endDate).endOf('day');
  
        filteredData = filteredData.filter(form => {
          const formDate = moment(form.createdDateTime * 1000);
          return formDate.isBetween(start, end);
        });
      }

    setContactForms(filteredData);
  };

  const handleClearFilters = () => {
    setCityFilter('');
    setStartDate();
    setEndDate();
    setContactForms(allContactForms);
  };

  const TABLE_HEAD = [
    { id: 'requestID', label: "Talep ID", alignRight: false },
    { id: 'companyName', label: "Firma Adı", alignRight: false, format: (value, row) => row.dataJSON.companyName },
    { id: 'authorizedPersonName', label: "Yetkili Kişi", alignRight: false, format: (value, row) => row.dataJSON.authorizedPersonName },
    { id: 'email', label: "E-posta", alignRight: false, format: (value, row) => row.dataJSON.email },
    { id: 'city', label: "Şehir", alignRight: false, format: (value, row) => row.dataJSON.city },
    {
      id: 'createdDateTime',
      label: "Oluşturma Tarihi",
      alignRight: false,
      format: (value, row) => moment(row.createdDateTime * 1000).format('DD/MM/YYYY')
    },
    {
      id: 'actions',
      label: 'Detaylar',
      alignRight: true,
      format: (value, row) => (
        <Button
          variant="outlined"
          size="small"
          onClick={() => handleOpenModal(row)}
        >
          Görüntüle
        </Button>
      ),
    },
  ];

  useEffect(() => {
    setLoading(true);
    Services.GetContactForms()
      .then((res) => {
        if (res.data.status && Array.isArray(res.data.result)) {
          setContactForms(res.data.result);
          setAllContactForms(res.data.result);
        } else {
          setContactForms([]);
          setAllContactForms([]);
          console.error("Veri alınamadı veya format hatalı:", res.data.message);
        }
      })
      .catch((err) => {
        console.error("Servis hatası:", err);
        setContactForms([]);
        setAllContactForms([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  return (
    <>
      <Helmet>
        <title> İletişim Formları </title>
      </Helmet>

      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
        <Typography variant="h4" gutterBottom>
          İletişim Formları
        </Typography>
      </Stack>

      <Card sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <TextField
            label="Şehir Ara"
            variant="outlined"
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
            sx={{ flex: 1, width: '100%', height: '56px' }}
          />
          <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={"tr"}>
              <DatePicker
                  format="DD/MM/YYYY"
                  label="Başlangıç Tarihi"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
              />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={"tr"}>
              <DatePicker
                  format="DD/MM/YYYY"
                  label="Bitiş Tarihi"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  disabled={!startDate}
                  minDate={moment(startDate)}
              />
          </LocalizationProvider>
          <Box>
            <Button variant="contained" onClick={handleFilter} sx={{ mr: 1 }}>
              Filtrele
            </Button>
            <Button variant="outlined" onClick={handleClearFilters}>
              Temizle
            </Button>
          </Box>
        </Stack>
      </Card>

      {loading ? (
        <Stack sx={{ width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center', display: 'flex' }}>
          <CircularProgress color="inherit" />
        </Stack>
      ) : (
        <Card>
          <DynamicTable
            data={contactForms}
            headers={TABLE_HEAD}
            initialOrder={'createdDateTime'}
            initalOrderDirection={'desc'}
            isOrdered={true}
          />
        </Card>
      )}

      {selectedFormData && (
        <Dialog
          open={isModalOpen}
          onClose={handleCloseModal}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Başvuru Detayları</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Firma Bilgileri</Typography>
                <Typography variant="body2"><b>Firma Adı:</b> {selectedFormData.companyName}</Typography>
                <Typography variant="body2"><b>Yetkili Kişi:</b> {selectedFormData.authorizedPersonName}</Typography>
                <Typography variant="body2"><b>Telefon:</b> {selectedFormData.authorizedPersonPhoneNumber}</Typography>
                <Typography variant="body2"><b>E-posta:</b> {selectedFormData.email}</Typography>
                <Typography variant="body2"><b>İşletme Türü:</b> {selectedFormData.businessType}</Typography>
                <Typography variant="body2"><b>Resmi Ad:</b> {selectedFormData.businessOfficialName}</Typography>
                <Typography variant="body2"><b>Resmi Sahip:</b> {selectedFormData.businessOfficialOwner}</Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Adres Bilgileri</Typography>
                <Typography variant="body2"><b>İl:</b> {selectedFormData.city}</Typography>
                <Typography variant="body2"><b>İlçe:</b> {selectedFormData.district}</Typography>
                <Typography variant="body2"><b>Mahalle:</b> {selectedFormData.neighborhood}</Typography>
                <Typography variant="body2"><b>Açık Adres:</b> {selectedFormData.address}</Typography>
                <Typography variant="body2"><b>Yol Tarifi:</b> {selectedFormData.directionAddress}</Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                 <Typography variant="h6" gutterBottom>Ek Bilgiler</Typography>
                 <Typography variant="body2"><b>Başka Şube Var mı?:</b> {selectedFormData.hasOtherBranch}</Typography>
                 <Typography variant="body2"><b>Tercih Edilen Servis:</b> {selectedFormData.whichService}</Typography>
                 <Typography variant="body2"><b>Yeterli Alan Var mı?:</b> {selectedFormData.hasEnoughSpace}</Typography>
                 <Typography variant="body2"><b>Bilgisayar Var mı?:</b> {selectedFormData.hasComputer}</Typography>
                 <Typography variant="body2"><b>İnternet Var mı?:</b> {selectedFormData.hasInternet}</Typography>
                 <Typography variant="body2"><b>Kamera Var mı?:</b> {selectedFormData.hasCamera}</Typography>
                 <Typography variant="body2"><b>Resmi Tatillerde Çalışıyor mu?:</b> {selectedFormData.worksOnHolidays}</Typography>
                 <Typography variant="body2"><b>Başka Üyelik Var mı?:</b> {selectedFormData.hasOtherMembership}</Typography>
              </Grid>
               <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Çalışma Saatleri</Typography>
                 {formatWorkingHours(selectedFormData.workingHours)}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal}>Kapat</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}